import { Link } from 'react-router';
import { ApiError } from '../api/client';
import { InlineError } from '../components/InlineError';
import { RelativeTime } from '../components/RelativeTime';
import { Skeleton } from '../components/Skeleton';
import { StateBadge } from '../components/StateBadge';
import type { AdminIntegrationConnection, Job, Run, User } from '../types';

export function AdminShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-8 px-6 py-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function AdminSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      <div className="overflow-x-auto border-y border-slate-200 bg-white">{children}</div>
    </section>
  );
}

export function AdminError({
  subject,
  error,
  onRetry,
}: {
  subject: string;
  error: unknown;
  onRetry: () => void;
}) {
  // A 403 means the viewer isn't an admin at all — name that directly instead
  // of surfacing the raw error message.
  if (error instanceof ApiError && error.status === 403) {
    return <InlineError heading="Admin access required." onRetry={onRetry} className="m-4" />;
  }
  return (
    <InlineError
      heading={`Couldn't load ${subject}`}
      error={error}
      onRetry={onRetry}
      className="m-4"
    />
  );
}

export function AdminSkeletonRows({ rows = 4 }: { rows?: number }) {
  return <Skeleton variant="rows" rows={rows} className="space-y-2 p-4" />;
}

export function DetailList({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 p-4 text-sm">
      {rows.map(([label, value]) => (
        <FragmentRow key={label} label={label} value={value} />
      ))}
    </dl>
  );
}

function FragmentRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="min-w-0 text-slate-800">{value}</dd>
    </>
  );
}

// Links to a user's admin detail page, showing their email when known and
// falling back to the raw user id (kept mono-spaced) when it isn't. The id is
// always available on hover via the title attribute. `userEmails` is a
// userId -> email lookup the caller builds from whatever user data it has.
export function UserLink({ userId, email }: { userId: string; email?: string }) {
  return (
    <Link
      to={`/admin/users/${userId}`}
      title={userId}
      className={`text-xs text-slate-600 hover:underline ${email ? '' : 'font-mono'}`}
    >
      {email ?? userId}
    </Link>
  );
}

export function UsersTable({ users }: { users: User[] }) {
  if (users.length === 0) return <Empty label="No users found." />;
  return (
    <table className="min-w-[44rem] table-fixed text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-4 py-2">Email</th>
          <th className="w-52 px-4 py-2">User ID</th>
          <th className="w-32 px-4 py-2">Updated</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {users.map((user) => (
          <tr key={user.id} className="hover:bg-slate-50">
            <td className="truncate px-4 py-3">
              <Link to={`/admin/users/${user.id}`} className="text-slate-950 hover:underline">
                {user.email}
              </Link>
            </td>
            <td className="truncate px-4 py-3 font-mono text-xs text-slate-500">{user.id}</td>
            <td className="px-4 py-3 text-slate-500">
              <RelativeTime iso={user.updatedAt} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AdminJobsTable({
  jobs,
  userEmails,
}: {
  jobs: Job[];
  userEmails?: ReadonlyMap<string, string>;
}) {
  if (jobs.length === 0) return <Empty label="No jobs found." />;
  return (
    <table className="min-w-[56rem] table-fixed text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="w-28 px-4 py-2">State</th>
          <th className="px-4 py-2">Job</th>
          <th className="w-36 px-4 py-2">User</th>
          <th className="w-24 px-4 py-2">Source</th>
          <th className="w-32 px-4 py-2">Updated</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {jobs.map((job) => (
          <tr key={job.id} className="hover:bg-slate-50">
            <td className="px-4 py-3">
              <StateBadge state={job.state} />
            </td>
            <td className="truncate px-4 py-3">
              <Link to={`/admin/jobs/${job.id}`} className="text-slate-950 hover:underline">
                {job.repo || job.jiraIssueKey || job.id}
              </Link>
              {job.jiraIssueKey && (
                <div className="mt-0.5 text-xs text-slate-500">{job.jiraIssueKey}</div>
              )}
            </td>
            <td className="truncate px-4 py-3">
              <UserLink userId={job.userId} email={userEmails?.get(job.userId)} />
            </td>
            <td className="px-4 py-3 text-slate-600">{job.source}</td>
            <td className="px-4 py-3 text-slate-500">
              <RelativeTime iso={job.updatedAt} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AdminRunsTable({
  runs,
  userEmails,
}: {
  runs: Run[];
  userEmails?: ReadonlyMap<string, string>;
}) {
  if (runs.length === 0) return <Empty label="No runs found." />;
  return (
    <table className="min-w-[56rem] table-fixed text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="w-28 px-4 py-2">State</th>
          <th className="px-4 py-2">Run</th>
          <th className="w-36 px-4 py-2">Job</th>
          <th className="w-36 px-4 py-2">User</th>
          <th className="w-32 px-4 py-2">Updated</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {runs.map((run) => (
          <tr key={run.id} className="hover:bg-slate-50">
            <td className="px-4 py-3">
              <StateBadge state={run.state} />
            </td>
            <td className="truncate px-4 py-3">
              <Link
                to={`/jobs/${run.jobId}/runs/${run.id}`}
                className="font-mono text-slate-950 hover:underline"
              >
                run {run.id}
              </Link>
              {run.retryCount > 0 && (
                <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">
                  retry {run.retryCount}
                </span>
              )}
            </td>
            <td className="truncate px-4 py-3">
              <Link
                to={`/admin/jobs/${run.jobId}`}
                className="font-mono text-xs text-slate-600 hover:underline"
              >
                {run.jobId}
              </Link>
            </td>
            <td className="truncate px-4 py-3">
              <UserLink userId={run.userId} email={userEmails?.get(run.userId)} />
            </td>
            <td className="px-4 py-3 text-slate-500">
              <RelativeTime iso={run.updatedAt} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function IntegrationsTable({
  integrations,
  deletingType,
  onDelete,
}: {
  integrations: AdminIntegrationConnection[];
  deletingType?: string;
  onDelete?: (integration: AdminIntegrationConnection) => void;
}) {
  if (integrations.length === 0) return <Empty label="No integrations found." />;
  return (
    <table className="min-w-[48rem] table-fixed text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="w-32 px-4 py-2">Type</th>
          <th className="px-4 py-2">Account</th>
          <th className="w-32 px-4 py-2">Status</th>
          <th className="w-32 px-4 py-2">Refreshed</th>
          {onDelete && <th className="w-28 px-4 py-2">Actions</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {integrations.map((integration) => (
          <tr key={integration.id} className="hover:bg-slate-50">
            <td className="px-4 py-3 font-semibold text-slate-800">
              {integration.integrationType}
            </td>
            <td className="truncate px-4 py-3">
              {integration.externalAccountLogin ?? integration.externalAccountId}
            </td>
            <td className="px-4 py-3 text-slate-600">{integration.status}</td>
            <td className="px-4 py-3 text-slate-500">
              {integration.lastRefreshedAt ? (
                <RelativeTime iso={integration.lastRefreshedAt} />
              ) : (
                'never'
              )}
            </td>
            {onDelete && (
              <td className="px-4 py-3">
                <button
                  type="button"
                  aria-label={`Delete ${integration.integrationType} integration`}
                  onClick={() => onDelete(integration)}
                  disabled={deletingType === integration.integrationType}
                  className="rounded border border-red-300 bg-white px-2 py-1 text-xs font-semibold text-red-700 hover:border-red-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {deletingType === integration.integrationType ? 'Deleting...' : 'Delete'}
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Empty({ label }: { label: string }) {
  return <p className="p-4 text-sm text-slate-500">{label}</p>;
}
